/**
 * Indian vehicle registration formats: normalisation + validation.
 *
 * India has two structurally different plate formats in active use. The pre-1989 format
 * (`MYS 1234`) never went away — it is common on tractors and trailers doing farm-gate pickups —
 * so a "new format only" rule blocks real arrivals at the gate (vnr-prod measured 91 of 7,315
 * real plates wrongly rejected). The stored value and the search term normalise identically.
 */

import { DomainError } from "./errors";

export type VehicleFormatKey =
  "new" | "old" | "bh" | "temporary" | "defence" | "diplomatic";

export const VEHICLE_FORMATS: Record<
  VehicleFormatKey,
  { label: string; pattern: RegExp }
> = {
  new: {
    label: "New (post-1989, e.g. MH 12 AB 1234)",
    pattern: /^[A-Z]{2}\d{1,2}[A-Z]{0,3}\d{1,4}$/,
  },
  old: { label: "Old (pre-1989, e.g. MYS 1234)", pattern: /^[A-Z]{3}\d{1,4}$/ },
  bh: {
    label: "Bharat series (e.g. 21 BH 2345 AA)",
    pattern: /^\d{2}BH\d{4}[A-Z]{1,2}$/,
  },
  // A subset of `new`; its own pattern so a form can untick `new` and still accept trade plates.
  temporary: {
    label: "Temporary / trade certificate (e.g. MH 12 TC 1234)",
    pattern: /^[A-Z]{2}\d{1,2}TC\d{1,4}$/,
  },
  defence: {
    label: "Defence (e.g. 04 A 12345 X)",
    pattern: /^\d{2}[A-Z]\d{5}[A-Z]$/,
  },
  diplomatic: {
    label: "Diplomatic / UN (e.g. 13 CD 1234)",
    pattern: /^\d{1,3}(?:CD|UN)\d{1,4}$/,
  },
};

export const VEHICLE_FORMAT_KEYS = Object.keys(
  VEHICLE_FORMATS
) as VehicleFormatKey[];

export type VehicleValidationMode = "off" | "warn" | "enforce";

/**
 * The one stored form of a plate: letters and digits only, uppercase (`MH 07 AT 7878`, `mh-07-at.7878`
 * → `MH07AT7878`, user 2026-11-04). Every separator an operator or a feed puts in goes. Idempotent.
 */
export function normalizeVehicleNumber(
  value: string | null | undefined
): string {
  return (value ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

function resolveKeys(allowed?: readonly string[] | null): VehicleFormatKey[] {
  // Empty/absent means "all". Unknown keys are ignored rather than failing shut: a stale key
  // left by a rename must not brick every arrival on that form.
  const keys = (allowed ?? []).filter(
    (k): k is VehicleFormatKey => k in VEHICLE_FORMATS
  );
  return keys.length ? keys : VEHICLE_FORMAT_KEYS;
}

/** True when `value` matches ANY of the allowed formats. */
export function validateVehicleNumber(
  value: string | null | undefined,
  allowed?: readonly string[] | null
): boolean {
  const normalized = normalizeVehicleNumber(value);
  if (!normalized) return false;
  return resolveKeys(allowed).some((k) =>
    VEHICLE_FORMATS[k].pattern.test(normalized)
  );
}

/** Human labels for the allowed formats, for error messages. */
export function vehicleFormatLabels(
  allowed?: readonly string[] | null
): string[] {
  return resolveKeys(allowed).map((k) => VEHICLE_FORMATS[k].label);
}

/**
 * Normalise a plate and apply a form's rule to it: `enforce` refuses a plate matching none of the
 * form's formats, `warn` reports it, `off` ignores it. A blank plate is not judged — whether one is
 * required is the caller's rule, not the format's.
 *
 * Shared so the gate and the arrival judge a plate identically: a truck the gate lets in under a
 * form's rule must never be refused by that same form at booking.
 */
export function assertVehicleNumber(
  value: string,
  rule: {
    vehicleValidation: VehicleValidationMode;
    vehicleFormats: readonly string[];
  },
  field = "transport.vehicleNumber"
): { normalised: string; formatUnknown: boolean } {
  const normalised = normalizeVehicleNumber(value);
  if (!normalised || rule.vehicleValidation === "off")
    return { normalised, formatUnknown: false };
  if (validateVehicleNumber(normalised, rule.vehicleFormats))
    return { normalised, formatUnknown: false };
  if (rule.vehicleValidation === "enforce")
    throw new DomainError(
      "vehicle_number_invalid",
      `Vehicle number '${value}' matches none of the accepted formats: ${vehicleFormatLabels(rule.vehicleFormats).join("; ")}.`,
      field
    );
  return { normalised, formatUnknown: true };
}
